import { useCallback, useEffect, useMemo } from "react";

import type { Site } from "../typings/Site";
import { sortMobileStagesToEnd } from "../utils/sortMobileStagesToEnd";
import { useCreateSitesDryRunQuery } from "../useCreateSitesDryRunQuery";
import { useSiteStages } from "react-migration/domains/sites/hooks/useSiteStages";
import { useLocalStorage } from "react-migration/lib/persistence/state/useLocalStorage";
import { SiteStage } from "react-migration/domains/sites/typings/SiteStage";

export const NO_SELECTION = "NO_SELECTION";

const isValidStageId = (stageId: string | null, stagesOrdered: SiteStage[]): boolean => {
  return Boolean(stageId && stagesOrdered?.some((stage) => stage._id === stageId));
};

export const computeSelectedStageId = (stagesOrdered: SiteStage[], storedStageId: string | null): string => {
  if (!stagesOrdered?.length) return NO_SELECTION;

  if (isValidStageId(storedStageId, stagesOrdered)) {
    return storedStageId!;
  }

  return stagesOrdered[0]._id;
};

export const shouldPersistStageId = (stageId: string, stagesOrdered: SiteStage[]): boolean => {
  return stageId !== NO_SELECTION && isValidStageId(stageId, stagesOrdered);
};

type useCheckStagesParams = {
  sites: Site[];
  disableDryRun?: boolean;
  siteMatchType?: "EXACT" | "INTERSECTS_POINT";
};

export const useCheckStages = ({ sites, disableDryRun, siteMatchType }: useCheckStagesParams) => {
  const [storedStageId, setStoredStageId] = useLocalStorage<string | null>("lastSelectedSiteStage", null);
  const { stages, loading: isStagesLoading, networkStatus, error: stagesError } = useSiteStages();
  const stagesOrdered = useMemo(() => (stages ? [...stages].sort(sortMobileStagesToEnd) : []), [stages]);

  const selectedStageId = useMemo(
    () => computeSelectedStageId(stagesOrdered, storedStageId),
    [stagesOrdered, storedStageId]
  );

  useEffect(() => {
    if (!stagesOrdered?.length) return;

    // Sync localStorage with computed selected stage
    // This handles: initial null state, invalid stored stage, or stage deletion
    if (storedStageId !== selectedStageId) {
      setStoredStageId(selectedStageId);
    }
  }, [stagesOrdered, storedStageId, selectedStageId, setStoredStageId]);

  const setSelectedStageId = useCallback(
    (stageId: string) => {
      if (shouldPersistStageId(stageId, stagesOrdered)) {
        setStoredStageId(stageId);
      }
    },
    [stagesOrdered, setStoredStageId]
  );

  const {
    data,
    loading,
    error: dryRunError,
  } = useCreateSitesDryRunQuery({
    shouldWait: isStagesLoading || disableDryRun,
    sites: sites,
    siteMatchType,
  });

  return {
    isError: dryRunError || stagesError,
    data,
    loading,
    selectedStageId,
    setSelectedStageId,
    stages: stagesOrdered,
    isStagesLoading,
    networkStatus,
  };
};
